import Cookie from "js-cookie";
import { FormEvent, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_API_VERSION } from "~/constants/config/common";
import { PAGE_ROUTES } from "~/constants/pages";
import { setCookie } from "~/helpers/cookie";
import { setNewRelicPageAction, setTrackingCustomAttributes } from "~/helpers/newrelic";
import { isNonEmptyObject } from "~/helpers/objectHelper";
import { isNonEmptyString } from "~/helpers/stringHelpers";
import { useRunServiceability } from "~/hooks/useRunServiceability";
import { FuelContext } from "~/providers/FuelProvider";
import { resetAddressRoute, setAddress } from "~/redux/actions/address";
import { removeACP, setBuyflowRedirectDelay, setCookiePass, setPageLoading, setResume } from "~/redux/actions/common";
import { setNotifications } from "~/redux/actions/global";
import { Address, ServiceabilityRequest } from "~/types/address";
import { ReduxState } from "~/types/redux";

interface ReturnType {
  apiVersion: string;
  selectedAddress: Address;
  installAddress: Address | Record<string, unknown>;
  setSelectedAddress: (address: Address) => void;
  handleFormSubmit: (event: FormEvent<HTMLFormElement>, onComplete?: () => Promise<void>) => Promise<void>;
  handleInputChange: (event: FormEvent<HTMLInputElement>) => void;
  buttonState: {
    disabled: boolean;
    loading: boolean;
  };
  submitted: boolean;
}

const useAddressCheck = (): ReturnType => {
  const [submitted, setSubmitted] = useState(false);
  const { address, tracking, acp, monarch, landingPage } = useSelector((store: ReduxState) => store);
  const [selectedAddress, setSelectedAddress] = useState<Address>({
    addressKey: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    parsedAddress: {},
  });
  const { router, trackEvent } = useContext(FuelContext);
  const dispatch = useDispatch();
  const { runServiceability, loading: serviceabilityLoading } = useRunServiceability();

  setTrackingCustomAttributes(tracking);

  const apiVersion = (monarch?.apiVersion as string) || DEFAULT_API_VERSION;
  const installZip = address?.install?.zip ?? "none";

  useEffect(() => {
    // set zip cookie for preamp
    setCookie("zip", installZip, { otGroup: "C0004" });
  }, [installZip]);

  useEffect(() => {
    if (landingPage?.persisted === false) {
      trackEvent({
        action: "elementViewed",
        data: {
          elementType: "CART STATE",
          location: "address_check_page",
          position: "PERSISTED FROM MLP",
          text: "DIFF ADDRESS",
        },
      });
    }
  }, [landingPage?.persisted]);

  // ACP or Cookie pass flow.
  useEffect(() => {
    let timeoutId = null;
    // TEMPORARY FIX: This forces us to wait for Cohesion to load by checking monarch.apiVersion.
    // Remove this workaround when we find a better solution
    if (monarch?.apiVersion && !monarch?.enabled) {
      const hasPredictiveCookie = Boolean(Cookie.get("frontierSiteDetailPredictive"));
      const hasValidInstallAddress = Boolean(isNonEmptyObject(address?.install) && address?.install?.addressKey);
      const cookiePassFlow = hasValidInstallAddress && hasPredictiveCookie;
      const acpFlow = Boolean(acp?.addressKey && acp?.env && acp?.controlNumber);
      const acFlow = Boolean(address?.install && Cookie.get("address"));

      const shouldRunServiceability = acpFlow || cookiePassFlow || acFlow;
      dispatch(setCookiePass(cookiePassFlow));

      if (shouldRunServiceability) {
        dispatch(setResume("resumeEnabled", false));
        dispatch(setPageLoading(true));
        timeoutId = setTimeout(() => {
          dispatch(setPageLoading(false));
        }, 5000);

        clearTimeout(timeoutId);

        const payload: ServiceabilityRequest = acpFlow
          ? {
              address: {
                addressKey: acp.addressKey,
                env: acp.env,
                controlNumber: acp.controlNumber,
              },
            }
          : {
              address: address.install,
              rawUserString: address.install.rawUserInput,
            };

        runServiceability(payload);

        Cookie.remove("frontierSiteDetailPredictive", {
          path: "/",
          domain: window?.location?.hostname === "localhost" ? "localhost" : ".frontier.com",
        });
        dispatch(removeACP());
      }
    }

    // If the component unmounts, clear the timeout
    return () => {
      clearTimeout(timeoutId);
    };
  }, [monarch?.apiVersion, monarch.determineResumeQuote]);

  // Update address if updated in the redux state.
  useEffect(() => {
    if (!serviceabilityLoading && isNonEmptyObject(address?.install) && isNonEmptyString(address?.install?.address)) {
      setSelectedAddress(address?.install);
    }
  }, [address?.install?.address]);

  // Prefetch the next page chunk.
  useEffect(() => {
    router.prefetch(PAGE_ROUTES.RECOMMENDATION);
    router.prefetch(PAGE_ROUTES.PLAN_PACKAGE);
    router.prefetch(PAGE_ROUTES.BUY_FLOW_REDIRECT);
  }, []);

  // Setting the buyflow redirect delay. Doing this as separate state outside of Monarch
  // so it doesn't get overridden on a page change and the value is ready immediately on pageload
  // on the Confirmation page.
  useEffect(() => {
    if (typeof monarch?.buyflowRedirectDelay === "number") {
      dispatch(setBuyflowRedirectDelay(monarch.buyflowRedirectDelay));
    }
  }, [monarch?.buyflowRedirectDelay]);

  // Event Handlers
  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>, onComplete?: () => Promise<void>) => {
    setSubmitted(true);
    event.preventDefault();
    dispatch(resetAddressRoute());
    // Hides error banner when running new address
    dispatch(setNotifications());

    trackEvent({
      action: "elementClicked",
      data: {
        text: "Check availability",
      },
      event,
    });
    setNewRelicPageAction("addressFormSubmitClicked");

    dispatch(setAddress("install", selectedAddress));

    dispatch(setAddress("install", selectedAddress));

    // Check if address is in AG states (NV, OH, MN, PA) and set cookie
    const AGStates = ["NV", "OH", "MN", "PA"];
    const isAGState = selectedAddress.state && AGStates.includes(selectedAddress.state);
    if (isAGState) {
      setCookie("isAGState", "true");
    }

    const serviceabilityResponse = await runServiceability({
      address: selectedAddress,
      rawUserString: selectedAddress?.rawUserInput,
    });
    if (!serviceabilityResponse || serviceabilityResponse.success === false) return;

    if (onComplete) {
      await onComplete();
    }
  };

  const handleInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;

    setSelectedAddress({
      ...selectedAddress,
      [name]: value,
    });
  };

  return {
    apiVersion,
    selectedAddress,
    installAddress: address?.install || {},
    setSelectedAddress,
    handleFormSubmit,
    handleInputChange,
    buttonState: {
      disabled: !selectedAddress.addressKey,
      loading: serviceabilityLoading,
    },
    submitted,
  };
};

export default useAddressCheck;
